import { createContext, useContext } from 'react';

// Contesto del wizard di prenotazione (/send-request/:activityId/*).
// Il provider vive in BookingWizardLayout: fornisce i dati attività, lo stato della
// prenotazione (persistito in sessionStorage per sopravvivere a back/refresh) e gli helper.

export const BookingWizardContext = createContext(null);
export const useBookingWizard = () => useContext(BookingWizardContext);

// Stato iniziale della prenotazione. participants[0] è chi prenota (nome+cognome+nascita);
// i successivi richiedono solo la data di nascita (name/surname restano vuoti).
// selections: opzioni di prezzo scelte con relativa quantità. Per flat/perHour la selezione
// è sempre singola; per perPerson si possono COMBINARE più opzioni nella stessa prenotazione
// (es. "2 × adulto + 2 × bambino" — feature 2026-09).
export const EMPTY_BOOKING = {
    selections: [],          // [{ bundleId, quantity }] — quantity null per flat
    slotId: null,            // slot fisso scelto (solo bookingType 'fixedSlots')
    preferredDate: null,
    preferredTime: null,
    alternativeSlots: '',    // testo libero, solo bookingType 'free'
    participants: [{ name: '', surname: '', birthdate: '' }],
    customFieldAnswers: {},  // { [label]: risposta }
    observations: '',
    sent: false
};

// Limite dei campi liberi, speculare al server (BOOKING_FREE_TEXT_MAX_LENGTH)
export const FREE_TEXT_MAX_LENGTH = 250;

// Migra lo stato salvato in sessionStorage prima del supporto multi-opzione
// (shape { bundleId, quantity }) verso la nuova shape { selections }.
export const migrateStoredBooking = (stored) => {
    if (!stored || Array.isArray(stored.selections)) return stored;
    const { bundleId, quantity, ...rest } = stored;
    return { ...rest, selections: bundleId ? [{ bundleId, quantity: quantity ?? null }] : [] };
};

// Selezioni risolte contro le opzioni dell'attività: [{ bundle, quantity }].
// Le selezioni la cui opzione è stata nel frattempo rimossa dall'admin vengono scartate.
export const getSelectedBundles = (activityData, booking) => {
    if (!activityData) return [];
    return (booking.selections || [])
        .map(selection => {
            const bundle = (activityData.price || []).find(element => element._id === selection.bundleId);
            return bundle ? { bundle, quantity: selection.quantity } : null;
        })
        .filter(Boolean);
};

// Prima opzione selezionata (per i consumer che ragionano a selezione singola: tipo, maxQty flat).
export const getSelectedBundle = (activityData, booking) =>
    getSelectedBundles(activityData, booking)[0]?.bundle || null;

// Totale mostrato nel wizard (somma di tutte le selezioni); il server lo ricalcola comunque.
export const computeTotal = (activityData, booking) => {
    const selected = getSelectedBundles(activityData, booking);
    if (selected.length === 0) return null;
    let total = 0;
    for (const { bundle, quantity } of selected) {
        const type = bundle.type || 'flat';
        if (type === 'flat') {
            total += Number(bundle.value);
        } else {
            if (!quantity) return null;
            total += Number(bundle.value) * quantity;
        }
    }
    return total;
};

// Numero di partecipanti implicato dalle selezioni perPerson (Σ quantità);
// null se la selezione non è perPerson (flat/perHour: partecipanti liberi).
export const getPerPersonCount = (activityData, booking) => {
    const selected = getSelectedBundles(activityData, booking);
    if (selected.length === 0) return null;
    if ((selected[0].bundle.type || 'flat') !== 'perPerson') return null;
    return selected.reduce((sum, { quantity }) => sum + (Number(quantity) || 0), 0);
};

// --- Completezza degli step (usata da guardie e bottoni Prosegui) ---

const isSelectionQuantityValid = (bundle, quantity) => {
    const type = bundle.type || 'flat';
    if (type === 'flat') return true;
    const minQty = Number(bundle.minQty) >= 1 ? Number(bundle.minQty) : 1;
    return Number.isInteger(quantity) && quantity >= minQty &&
        (bundle.maxQty == null || quantity <= Number(bundle.maxQty));
};

export const isOptionStepComplete = (activityData, booking) => {
    const selected = getSelectedBundles(activityData, booking);
    if (selected.length === 0) return false;
    // Un'opzione selezionata non esiste più (rimossa dall'admin): si riparte dallo step opzione
    if (selected.length !== (booking.selections || []).length) return false;
    // Un'opzione selezionata è stata nel frattempo marcata sold out dall'admin: idem
    if (selected.some(({ bundle }) => bundle.isSoldOut)) return false;
    // Più selezioni insieme: ammesse solo se tutte perPerson (speculare al server)
    if (selected.length > 1 && !selected.every(({ bundle }) => (bundle.type || 'flat') === 'perPerson')) return false;
    return selected.every(({ bundle, quantity }) => isSelectionQuantityValid(bundle, quantity));
};

export const isScheduleStepComplete = (activityData, booking) => {
    if (!activityData) return false;
    if (activityData.bookingType === 'fixedSlots') {
        const slot = (activityData.slots || []).find(element => element._id === booking.slotId);
        return Boolean(slot && !slot.isSoldOut);
    }
    return Boolean(booking.preferredDate && booking.preferredTime);
};

const isParticipantComplete = (participant, index) => {
    if (!participant.birthdate) return false;
    if (index === 0) return Boolean(participant.name.trim() && participant.surname.trim());
    return true;
};

export const isParticipantsStepComplete = (activityData, booking) => {
    const selected = getSelectedBundles(activityData, booking);
    if (selected.length === 0) return false;
    if (booking.participants.length === 0) return false;
    // perPerson: il numero di partecipanti deve combaciare con la somma delle quantità scelte
    const perPersonCount = getPerPersonCount(activityData, booking);
    if (perPersonCount != null && booking.participants.length !== perPersonCount) return false;
    const single = selected.length === 1 ? selected[0].bundle : null;
    if (single && (single.type || 'flat') === 'flat' && single.maxQty != null &&
        booking.participants.length > Number(single.maxQty)) return false;
    return booking.participants.every(isParticipantComplete);
};

// Primo step non completo: destinazione delle guardie quando si atterra su uno step successivo.
export const getFirstIncompleteStep = (activityData, booking) => {
    if (!isOptionStepComplete(activityData, booking)) return 'opzione';
    if (!isScheduleStepComplete(activityData, booking)) return 'data';
    if (!isParticipantsStepComplete(activityData, booking)) return 'partecipanti';
    return 'riepilogo';
};

// Converte i partecipanti del wizard nella forma { amount, data } attesa dal server
// (stessa shape delle build app legacy → un solo formato lato server).
export const participantsToPeoplePayload = (participants) => {
    const data = {};
    participants.forEach((participant, index) => {
        data[`user-${index}`] = {
            name: participant.name.trim(),
            surname: participant.surname.trim(),
            birthdate: participant.birthdate
        };
    });
    return { amount: participants.length, data };
};
